package com.vamvisions.main.pages.projects

import com.vamvisions.main.theme.AppStyleSheet
import com.vamvisions.main.theme.NARROW_SCREEN
import com.vamvisions.main.theme.Tokens
import com.vamvisions.main.theme.cssVar
import org.jetbrains.compose.web.css.StyleSheet
import org.jetbrains.compose.web.css.media

/** Viewports at least 600 CSS pixels wide, where featured cards use their large layout. */
private const val WIDE_SCREEN = "(min-width: 600px)"

/**
 * The Projects page: intro, the featured cards ("Now"), the earlier apps in a grid and the call-to-action card, on
 * glass over the background. Sizes follow the Figma ProjectCard at an 18px root.
 */
object ProjectsStyles : StyleSheet() {

    /** The page content, as wide as the Figma frames' content. */
    val page by style {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "4rem")
        property("max-width", "53.33rem")
        property("margin-inline", "auto")
        media(NARROW_SCREEN) {
            self style {
                property("gap", "2.67rem")
            }
        }
        // The page's section titles are larger than elsewhere, with more room below.
        desc(self, className(AppStyleSheet.sectionTitle)) style {
            property("font-size", "1.89rem")
        }
        desc(self, className(AppStyleSheet.sectionHeading)) style {
            property("margin-bottom", "1.33rem")
        }
        desc(self, className(AppStyleSheet.section)) style {
            property("margin", "0")
        }
    }

    val intro by style {
        property("display", "flex")
        property("flex-direction", "column")
        property("align-items", "center")
        property("gap", "0.67rem")
        property("text-align", "center")
    }

    val introTitle by style {
        property("margin", "0")
        property("font-size", "clamp(1.8rem, 6vw, 2.6rem)")
        property("line-height", "1.2")
    }

    val introText by style {
        property("max-width", "43.33rem")
        property("margin", "0")
        property("font-size", "1.2rem")
        media(NARROW_SCREEN) {
            self style {
                property("font-size", "1rem")
            }
        }
    }

    /** The status messages and the sections share the page's vertical rhythm. */
    val content by style {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "4rem")
        media(NARROW_SCREEN) {
            self style {
                property("gap", "2.67rem")
            }
        }
    }

    /** The featured cards, one per row. */
    val featuredList by style {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "1.33rem")
        property("margin", "0")
        property("padding", "0")
        property("list-style", "none")
    }

    /** The earlier apps, two per row and as tall as each other; one column on phones. */
    val earlierGrid by style {
        property("display", "grid")
        property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
        property("gap", "1.33rem")
        property("margin", "0")
        property("padding", "0")
        property("list-style", "none")
        media(NARROW_SCREEN) {
            self style {
                property("grid-template-columns", "minmax(0, 1fr)")
            }
        }
    }

    val item by style {
        property("display", "flex")
        property("min-width", "0")
    }

    /**
     * A project card in its compact layout: the icon beside the meta line and name, then the description, skills and
     * links across the card. [cardFeatured] turns it into the large layout on wide screens.
     */
    val card by style {
        property("display", "grid")
        property("flex", "1")
        property("grid-template-columns", "3.56rem minmax(0, 1fr)")
        property("grid-template-areas", "\"icon head\" \"desc desc\" \"skills skills\" \"links links\"")
        property("align-content", "start")
        property("gap", "0.78rem 0.89rem")
        property("padding", "1.33rem")
        // The skills and links rows hold lists that bring no margin of their own.
        desc(self, className(AppStyleSheet.skillList)) style {
            property("grid-area", "skills")
            property("margin", "0")
        }
    }

    /** The large layout: the icon in its own column, beside every other part. */
    val cardFeatured by style {
        media(WIDE_SCREEN) {
            self style {
                property("grid-template-columns", "6.67rem minmax(0, 1fr)")
                property("grid-template-areas", "\"icon head\" \"icon desc\" \"icon skills\" \"icon links\"")
                property("gap", "0.56rem 1.56rem")
                property("padding", "1.56rem")
            }
        }
    }

    val icon by style {
        property("grid-area", "icon")
        property("display", "block")
        property("width", "3.56rem")
        property("height", "3.56rem")
        property("border-radius", "0.78rem")
        property("object-fit", "cover")
    }

    /** Head sits beside the compact icon, centred on it, and at the top beside the featured one. */
    val head by style {
        property("grid-area", "head")
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "0.22rem")
        property("align-self", "center")
        property("min-width", "0")
    }

    val meta by style {
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("align-items", "center")
        property("gap", "0.22rem 0.56rem")
        property("margin", "0")
        property("font-size", "0.78rem")
        property("font-weight", "600")
        property("color", cssVar(Tokens.COLOR_MUTED))
    }

    val name by style {
        property("margin", "0")
        property("font-size", "1.22rem")
        property("line-height", "1.25")
        property("overflow-wrap", "anywhere")
    }

    val description by style {
        property("grid-area", "desc")
        property("margin", "0")
        property("font-size", "0.83rem")
        property("line-height", "1.6")
    }

    /** Wrapping link pills: store pages, then the live website, the source code and the privacy policy. */
    val links by style {
        property("grid-area", "links")
        property("display", "flex")
        property("flex-wrap", "wrap")
        property("gap", "0.56rem 0.44rem")
        property("min-width", "0")
        desc(self, className(AppStyleSheet.linkPill)) style {
            property("max-width", "100%")
        }
    }

    init {
        // The featured card's larger parts, on wide screens only.
        media(WIDE_SCREEN) {
            ".$cardFeatured .$icon" style {
                property("width", "6.67rem")
                property("height", "6.67rem")
                property("border-radius", "1.44rem")
            }
            ".$cardFeatured .$head" style {
                property("gap", "0.56rem")
                property("align-self", "start")
            }
            ".$cardFeatured .$name" style {
                property("font-size", "1.56rem")
            }
            ".$cardFeatured .$description" style {
                property("font-size", "0.89rem")
            }
        }
        media(NARROW_SCREEN) {
            ".$page .${AppStyleSheet.sectionTitle}" style {
                property("font-size", "1.56rem")
            }
        }
    }
}
